import { ReactElement, useCallback, useEffect, useLayoutEffect, useMemo } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useParams } from 'react-router-dom'
import { useLatest } from 'react-use'
import { buildErrorMessage } from '@constants'
import { getSupportsOfElement } from '@domainUtils'
import { combineAllLoadsByLoadCaseAndPosition, mapValueKey } from '@editorUtils'
import {
  AnchorIndicatorMesh,
  PositionMeshConnector,
  SimpleLoadMesh,
  SimpleStiffeningMesh,
  TransmitterMesh,
  useModelClickListenerV2,
} from '@scene'
import { AxiosError } from 'axios'
import { filter, find, first, flatten, isArray, isUndefined, reject, toNumber } from 'lodash-es'
import { Vector3 } from 'three'
import { FloorplanLoadsMesh } from '@editor/pages/StructuralPlanning/components/FloorplanDrawer/components'
import { maxLineLoadMagnitude } from '@editor/pages/StructuralPlanning/components/FloorplanDrawer/components/FloorplanLineLoadsMesh/utils'
import { maxPointLoadMagnitude } from '@editor/pages/StructuralPlanning/components/FloorplanDrawer/components/FloorplanPointLoadsMesh/utils'
import { useTapelineStore, toImmutable } from '@modugen/ui-toolkit'
import { coordinateEventName } from '@ui/events'
import { enqueueSnackbar } from '@ui/snackbar'
import {
  ResultsViewTab,
  roofStoreyKey,
  useControlStore,
  useElementSelectionStore,
  useEditorViewStore,
  useModelStore,
  useResultsStore,
  useStiffeningStore,
  useTransmissionStore,
  useElementLoadStore,
} from '@editorStores'
import {
  useResultsSelection,
  useAllDomains,
  useElementTypes,
  useFloorplanClickListener,
  useGuidToElement,
  useHiddenElements,
  useHighlightPrimary,
  useHighlightSecondary,
  useModelClickListeners,
  useMultiSelection,
  usePositionGuidToWallGuid,
  usePositionVisibilityLookup,
  useSceneColoring,
  useSceneContextMenuAction,
  useSelectionMode,
  useSupportGuidToElement,
  useVerticalTransmitters,
} from '@editorHooks'
import { ADD_COMMENTS_SELECTION_KEY } from 'src/components/pages/Editor/components/AddCommentToolButton/useAddCommentTool'
import { memberToDomain } from 'src/components/pages/Editor/utils/positionToDomain'
import { SlabBeamsWithStepSize } from 'src/components/scene/SlabDesigns'
import SupportPositionIndicatorMesh from 'src/components/scene/SupportPositionIndicatorMesh'
import WindLoadCPEAreasMesh from 'src/components/scene/WindLoadCPEAreasMesh'
import {
  useHotkeys_0to9WithNumpadAndLongPress,
  useHotkeys_ShortAndLongPress,
} from 'src/hooks/useHotkeysShiftX'
import { useGetDesignForcesPerSupport } from 'src/state/api/generated/design-forces-per-support/design-forces-per-support'
import AlignmentHelper from '../../../StructuralPlanning/components/AlignmentHelper'
import { useFilterState } from '../../../StructuralPlanning/hooks/useFilterState'
import { useVerticalOpeningHelpers } from '../../../StructuralPlanning/hooks/useVerticalOpeningHelpers'
import useCornerConnectionAdditionalLoadProposalState from '../../hooks/useCornerConnectionAdditionalLoadProposalState'
import useDesignFormNavigationGuard from '../../hooks/useDesignFormNavigationGuard'
import useElementPositionType from '../../hooks/useElementPositionType'
import { useEnhanceLoadsWithDomain } from '../../hooks/useEnhanceLoadsWithDomain'
import { collectFokusGuidsForElement } from '../../hooks/useResults2FokusEffect'
import useShearAnchorChecks from '../../hooks/useShearAnchorChecks'
import useLoadsCombinedStore from '../../stores/useLoadsCombinedStore'
import { getWallGuidForStiffeningSegment } from '../../utils'
import { getReducingDomainLoads } from '../Results2AnchorDetailPane/utils'
import {
  buildHiddenSupportPositionPredicate,
  matchesRelativePosition,
} from '../Results2DetailPane/utils'
import { getShearAnchorDomainLoads } from '../Results2ShearAnchorDetailPane/utils'
import {
  CheckPositionSceneLabels,
  CornerConnectionProposalMesh,
  GroupingLabels,
  SupportSceneLabels,
} from './components'
import {
  createProposalConfig,
  getPositionVectorFromCheckPosition,
  interpolateShapeObject,
} from './misc'

/** Wall meshes set `userData.elementType` to model element types, not `ResultElementType`. */
type WallMeshElementType = 'inner_walls' | 'outer_walls'

const PREVIOUS_LOAD_OPACITY = 0.5

const Results2Scene = (): ReactElement => {
  const guardDesignFormNav = useDesignFormNavigationGuard()
  const { isSelectionMode, selectionModeKey, unsetSelectionMode } = useSelectionMode()
  const onModelClick = useModelClickListeners()

  const highlightedSecondaryIds = useElementSelectionStore(state => state.highlightedSecondaryIds)

  const selectedStandAloneIds = useElementSelectionStore(state => state.selectedStandAloneIds)
  const setSelectedStandaloneIds = useElementSelectionStore(state => state.setSelectedStandaloneIds)

  const positionVisibilityLookup = usePositionVisibilityLookup()

  const tab = useEditorViewStore(state => state.results.tab)
  const wallSubTab = useEditorViewStore(state => state.results.wallSubTab)
  const localTab = useEditorViewStore(state => state.results.localTab)
  const tracedLoads = useEditorViewStore(state => state.results.tracedLoads)
  const visibleLoadTypes = useEditorViewStore(state => state.results.visibleLoadTypes)
  const detailTab = useEditorViewStore(state => state.results.detailTab)
  const {
    selectedCheckPosition,
    selectedStiffeningSegment,
    selectedAnchor,
    selectedShearAnchor,
    selectedLoad,
    selectedConnector,
    selectedElements,
    activeMode,
    selectedVerticalTransmitter: verticalTransmitter,
    selectResultsStiffeningSegment: selectStiffeningSegment,
    selectResultsAnchor: selectAnchor,
    selectResultsLoad: selectLoad,
    selectResultsConnector: selectConnector,
    selectResultsPosition: selectPosition,
    selectResultsSurface: selectSurface,
    addResultsSelection: multiSelectElement,
    removeResultsSelection: removeMultiSelectElement,
    setResultsVerticalTransmitter: setVerticalTransmitter,
  } = useResultsSelection()

  const {
    proposalSelectionKey,
    selectWall,
    mutateAsync: mutateCornerConnectionProposal,
  } = useCornerConnectionAdditionalLoadProposalState()

  // CONTEXT ACTIONS

  const actionMode = useControlStore(state => state.actionMode)

  const getRipOfWall = (wallGuid: string) => {
    const standardRipChecks = useResultsStore.getState().standardRipChecks
    const checksForWall = filter(standardRipChecks, ripCheck => ripCheck.wall_guid === wallGuid)

    if (checksForWall.length !== 1) return

    const positionGuid = checksForWall[0].standard_rip_member.guid

    return positionGuid
  }

  const getWallGuidOfStiffeningSegment = (stiffeningSegmentGuid: string) => {
    const stiffeningIntervals = useStiffeningStore.getState().stiffeningIntervals
    const interval = find(stiffeningIntervals, { guid: stiffeningSegmentGuid })

    if (!interval) return

    return interval.element_guid
  }
  useSceneContextMenuAction({
    label: 'Standardrippen',
    action: (selectedElement: string | null) => {
      if (selectedElement) {
        const positionGuid = getRipOfWall(selectedElement)

        if (positionGuid) {
          selectPosition(positionGuid, 'walls', 'standard-rips')
        }
      }
    },
    available: selectedElement => {
      if (!selectedElement) return false

      const positionGuid = getRipOfWall(selectedElement)

      return !!positionGuid
    },
    group: 'results-tabs',
  })

  useSceneContextMenuAction({
    label: 'Standardrippen der Wand',
    action: (selectedElement: string | null) => {
      if (selectedElement) {
        const wallGuid = getWallGuidOfStiffeningSegment(selectedElement)

        if (!wallGuid) return

        const positionGuid = getRipOfWall(wallGuid)

        if (positionGuid) {
          selectPosition(positionGuid, 'walls', 'standard-rips')
        }
      }
    },
    available: (selectedElement, point, userDataActions, userData) => {
      return 'contextType' in userData && userData.contextType === 'stiffening-mesh'
    },
    group: 'results-tabs',
  })

  const getStiffeningSegmentOfWall = (wallGuid: string) => {
    const stiffeningIntervals = useStiffeningStore.getState().stiffeningIntervals
    const interval = find(stiffeningIntervals, { element_guid: wallGuid })

    if (!interval) return

    return interval.guid
  }

  useSceneContextMenuAction({
    label: 'Wandscheibe für diese Wand',
    action: (selectedElement: string | null) => {
      if (selectedElement) {
        const segmentGuid = getStiffeningSegmentOfWall(selectedElement)

        if (segmentGuid) {
          selectStiffeningSegment(segmentGuid)
        }
      }
    },
    available: selectedElement => {
      if (!selectedElement) return false

      const segmentGuid = getStiffeningSegmentOfWall(selectedElement)

      return !!segmentGuid
    },
    group: 'results-tabs',
  })

  useSceneContextMenuAction({
    label: 'Wandscheibe',
    action: (selectedElement: string | null) => {
      if (selectedElement) {
        selectStiffeningSegment(selectedElement)
      }
    },
    available: (selectedElement, point, userDataActions, userData) => {
      return 'contextType' in userData && userData.contextType === 'stiffening-mesh'
    },
    group: 'results-tabs',
  })

  // STORE

  const isTapelineActive = useTapelineStore(state => state.isActive)
  const allDomains = useAllDomains()

  const {
    model,
    visibleStoreys,
    availableStoreys,
    domains: modelDomains,
    setStoreyVisibility,
    setTypeVisibility,
    toggleSingleStoreyVisibility,
    showComments,
    setShowComments,
  } = useModelStore(state => state)
  const {
    rips,
    lintels,
    vertical_slabs: verticalSlabs,
    vertical_roof_slabs: verticalRoofSlabs,
  } = useModelStore(state => state.model)
  const anchorChecks = useResultsStore(state => state.anchorChecks)

  const addHiddenElementIds = useControlStore(state => state.addHiddenElementIds)
  const removeHiddenElementIds = useControlStore(state => state.removeHiddenElementIds)

  const stiffeningIntervals = useStiffeningStore(state => state.stiffeningIntervals)
  const resultStoreVisibility = useResultsStore(state => state.resultElementVisibility)
  const verticalTransmittersVisible = useControlStore(
    state => state.structuralPlanningElementVisibility.verticalTransmitters,
  )

  const tensileTransmissionGraph = useTransmissionStore(state => state.tensileTransmissionGraph)
  const verticalTransmissionGraph = useTransmissionStore(state => state.verticalTransmissionGraph)
  const selectedProposalAnchor = useResultsStore(state => state.selectedProposalAnchor)

  const {
    showVerticalSlabs,
    showVerticalRoofSlabs,
    showSlabs,
    showRoofSlabs,
    showPurlins,
    showSlabBeams,
    showRoofSlabBeams,
    showVerticalOpeningAlignmentHelpers,
  } = useFilterState()
  const verticalOpeningHelpers = useVerticalOpeningHelpers()

  const elementLoads = useResultsStore(state => state.elementLoads)
  const positionLoads = useResultsStore(state => state.positionLoads)
  const hiddenLoads = useResultsStore(state => state.hiddenLoads)
  const manualLoads = useElementLoadStore(state => state.loads)

  const memberCheckSettings = useResultsStore(state => state.memberCheckSettings)
  const highlightedCheck = useResultsStore(state => state.highlightedCheck)
  const elementTypes = useElementTypes()
  const hiddenElements = useHiddenElements()

  const hiddenElementIds = useControlStore(state => state.hiddenElementIds)
  const areaLoadsVisible = useControlStore(
    state => state.structuralPlanningElementVisibility.areaLoads,
  )
  const isAnchorCalculationMode = activeMode === 'tensile-connectors-table'
  const isAnchorsTabActive = tab === 'walls' && wallSubTab === 'anchors'

  // Relieving (compressing) loads of the selected anchor — rendered like element loads while the
  // "Anker" tab is active. Data comes from the per-support design forces (same query as the
  // anchor detail pane, shared via the react-query cache).
  const { projectId } = useParams()
  const anchorInterventionTableData = useResultsStore(state => state.anchorInterventionTableData)
  const anchorDesignForcesQuery = useGetDesignForcesPerSupport(projectId as string, {
    query: {
      enabled: isAnchorsTabActive && !!projectId,
      onError: (error: unknown) =>
        enqueueSnackbar(
          buildErrorMessage(error as AxiosError, 'Fehler beim Laden der Anker-Einwirkungen'),
          { variant: 'error', preventDuplicate: true, key: 'anchor-design-forces-error' },
        ),
    },
  })
  const anchorGuidToDomain = useMemo(() => mapValueKey(allDomains), [allDomains])
  const anchorReducingLoads = useMemo(() => {
    if (!isAnchorsTabActive || !selectedAnchor) return []
    const designForces = anchorDesignForcesQuery.data?.[selectedAnchor]
    const elementGuid =
      find(anchorInterventionTableData ?? [], { support_guid: selectedAnchor })?.element_guid ?? ''
    return getReducingDomainLoads(designForces, anchorGuidToDomain, elementGuid).filter(
      load =>
        (visibleLoadTypes[load.load_type as LoadType] ?? true) && !hiddenLoads.includes(load.guid),
    )
  }, [
    isAnchorsTabActive,
    selectedAnchor,
    anchorDesignForcesQuery.data,
    anchorInterventionTableData,
    anchorGuidToDomain,
    visibleLoadTypes,
    hiddenLoads,
  ])

  // Clicking a connector (support or target box) in the scene selects that anchor in the "Anker"
  // list; the guard mirrors the list's handleSelect so unsaved detail-pane edits block switching.
  const handleAnchorConnectorClick = useCallback(
    ({ guid }: ElementSupportItem) => {
      if (guid !== selectedAnchor && guardDesignFormNav()) return
      selectAnchor(guid)
    },
    [guardDesignFormNav, selectAnchor, selectedAnchor],
  )

  const isShearAnchorsTabActive = tab === 'walls' && wallSubTab === 'shear-anchors'
  const shearAnchorChecksQuery = useShearAnchorChecks()
  const shearAnchorLoads = useMemo(() => {
    if (!isShearAnchorsTabActive || !selectedShearAnchor) return []
    const loads = find(shearAnchorChecksQuery.data ?? [], {
      element_guid: selectedShearAnchor,
    })?.loads
    // The segment's loads live on its wall, so they are drawn on the wall's
    // domains -- same as the tensile anchor's relieving loads.
    const wallGuid = getWallGuidForStiffeningSegment(stiffeningIntervals, selectedShearAnchor) ?? ''
    return getShearAnchorDomainLoads(loads, anchorGuidToDomain, wallGuid).filter(
      load =>
        (visibleLoadTypes[load.load_type as LoadType] ?? true) && !hiddenLoads.includes(load.guid),
    )
  }, [
    isShearAnchorsTabActive,
    selectedShearAnchor,
    shearAnchorChecksQuery.data,
    stiffeningIntervals,
    anchorGuidToDomain,
    visibleLoadTypes,
    hiddenLoads,
  ])

  const standardRipChecks = useResultsStore(state => state.standardRipChecks)

  const { cornerConnectionState } = useCornerConnectionAdditionalLoadProposalState()

  const loads = useMemo(
    () => [...(elementLoads || []), ...(positionLoads || [])],
    [elementLoads, positionLoads],
  )

  const guidToElement = useGuidToElement()

  // DATA TRANSFORMATIONS

  const availableNonRoofStoreys = reject([...availableStoreys], storey => storey === roofStoreyKey)

  const selectedElement = useMemo(
    () => (selectedElements?.length === 1 ? selectedElements?.[0] : null),
    [selectedElements],
  )

  // In Results2, "local mode" is derived from single selection (no separate store flag).
  const isLocalMode = selectedElements?.length === 1

  const selectedElementGuidForSupports = useMemo(() => {
    if (!selectedElement) return null

    const slab = model.vertical_slabs.find(s => s.guid === selectedElement)
    if (slab?.beam?.guid) return slab.beam.guid

    const roofSlab = model.vertical_roof_slabs.find(s => s.guid === selectedElement)
    if (roofSlab?.beam?.guid) return roofSlab.beam.guid

    return selectedElement
  }, [model.vertical_slabs, model.vertical_roof_slabs, selectedElement])

  // Interpolate a point at relative position [0,1] along the currently selected
  // element. Shared by the support labels (A1, A2…) and the check-position
  // marker (*) so both sit on the same line.
  const resolvePositionAlongSelectedElement = useCallback(
    (rel: number): Vector3 | null => {
      if (!selectedElementGuidForSupports || !Number.isFinite(rel)) return null

      const slabBeam =
        verticalSlabs.find(s => s.beam?.guid === selectedElementGuidForSupports)?.beam ??
        verticalRoofSlabs.find(s => s.beam?.guid === selectedElementGuidForSupports)?.beam

      const lineShape =
        (model.beams.find(b => b.guid === selectedElementGuidForSupports) as ShapeObjectLine) ||
        (model.purlins.find(p => p.guid === selectedElementGuidForSupports) as ShapeObjectLine) ||
        (model.columns.find(c => c.guid === selectedElementGuidForSupports) as ShapeObjectLine) ||
        (slabBeam as unknown as ShapeObjectLine | undefined) ||
        (guidToElement[selectedElementGuidForSupports] as ShapeObjectLine | undefined)

      // Rips/lintels are position-guid keyed and store endpoints as start/end (not shape.start/end).
      const ripOrLintel =
        rips?.find(r => r.position_guid === selectedElementGuidForSupports) ??
        lintels?.find(l => l.position_guid === selectedElementGuidForSupports)

      let p: Vector3 | null = null
      if (lineShape?.shape?.start && lineShape?.shape?.end) {
        p = interpolateShapeObject(lineShape, rel)
      } else if (ripOrLintel?.start && ripOrLintel?.end) {
        const s = new Vector3(ripOrLintel.start.x, ripOrLintel.start.y, ripOrLintel.start.z)
        const e = new Vector3(ripOrLintel.end.x, ripOrLintel.end.y, ripOrLintel.end.z)
        p = s.lerp(e, rel)
      }

      if (!p) return null
      return p.add(new Vector3(0, 0, 0.2))
    },
    [
      guidToElement,
      model.beams,
      model.columns,
      model.purlins,
      rips,
      lintels,
      verticalSlabs,
      verticalRoofSlabs,
      selectedElementGuidForSupports,
    ],
  )

  const supportLabelItems = useMemo(() => {
    if (!selectedElementGuidForSupports || !verticalTransmissionGraph) return []

    // Prefer the same source as the detail pane (support configs) if available.
    const settingsForElement = memberCheckSettings.find(
      s => 'element_guid' in s && s.element_guid === selectedElementGuidForSupports,
    )

    const supportConfigs =
      settingsForElement &&
      'support_configs' in settingsForElement &&
      Array.isArray(settingsForElement.support_configs)
        ? settingsForElement.support_configs
        : []

    // Mirror the detail pane's "Auflager" filtering: for rips/columns the support
    // config that does not map to a supporting element (the "second support") is
    // hidden, so the scene labels stay in sync with the supports grid. Rebuild the
    // support-config → supporting-element-index map exactly as Results2DetailPane.
    const focusGuids = collectFokusGuidsForElement(
      verticalTransmissionGraph,
      selectedElementGuidForSupports,
    )
    const supportsOfRoot = getSupportsOfElement(
      selectedElementGuidForSupports,
      verticalTransmissionGraph,
    )
    const supportConfigIndexToElementIndex = supportConfigs.reduce<Record<number, number>>(
      (acc, config, index) => {
        const support = supportsOfRoot.find(({ relativePosition }) =>
          matchesRelativePosition(relativePosition, config.relative_position),
        )
        acc[index] = focusGuids.findIndex(guid => guid === support?.guid)
        return acc
      },
      {},
    )
    const isHiddenSupportPosition = buildHiddenSupportPositionPredicate(
      elementTypes[selectedElementGuidForSupports],
      supportConfigs,
      supportConfigIndexToElementIndex,
    )

    const relPositionsFromSettings = supportConfigs
      .map(c => (c && 'relative_position' in c ? c.relative_position : undefined))
      .filter((rp): rp is string => typeof rp === 'string')
      .filter(rp => !isHiddenSupportPosition(rp))

    const relPositions =
      relPositionsFromSettings.length > 0
        ? relPositionsFromSettings
        : getSupportsOfElement(selectedElementGuidForSupports, verticalTransmissionGraph).map(
            s => s.relativePosition,
          )

    const relPositionsSorted = relPositions.slice().sort((a, b) => Number(a) - Number(b))

    return relPositionsSorted
      .map((relativePosition, idx) => {
        const p = resolvePositionAlongSelectedElement(Number(relativePosition))
        if (!p) return null

        return {
          id: `${selectedElementGuidForSupports}-${relativePosition}`,
          relativePosition: String(relativePosition),
          p,
          n: idx + 1,
        }
      })
      .filter(
        (x): x is { id: string; relativePosition: string; p: Vector3; n: number } => x !== null,
      )
  }, [
    elementTypes,
    memberCheckSettings,
    selectedElementGuidForSupports,
    verticalTransmissionGraph,
    resolvePositionAlongSelectedElement,
  ])

  const checkMarkerItems = useMemo(() => {
    if (
      highlightedCheck?.kind !== 'check' ||
      !selectedElementGuidForSupports ||
      highlightedCheck.elementGuid !== selectedElementGuidForSupports
    ) {
      return []
    }

    const p = resolvePositionAlongSelectedElement(Number(highlightedCheck.relativePosition))
    if (!p) return []

    return [
      {
        id: `check-${selectedElementGuidForSupports}-${highlightedCheck.checkType}-${highlightedCheck.relativePosition}`,
        relativePosition: String(highlightedCheck.relativePosition),
        checkType: highlightedCheck.checkType,
        p,
      },
    ]
  }, [highlightedCheck, selectedElementGuidForSupports, resolvePositionAlongSelectedElement])

  const selectedStandardRipLoads = useMemo(() => {
    const selectedCheck = find(standardRipChecks, check => {
      return (
        selectedElements?.length === 1 && check.standard_rip_member.guid === selectedElements[0]
      )
    })

    if (!selectedCheck) return []

    const standardRipDomain = memberToDomain(selectedCheck.standard_rip_member)

    return selectedCheck.standard_rip_loads.map(load => {
      return {
        ...load,
        domain: standardRipDomain,
      } as PointLoadWithDomain
    })
  }, [standardRipChecks, selectedElements])

  // FILTERED DATA

  const wallDesignsInStorey = useMemo(
    () =>
      rips &&
      lintels &&
      filter([...rips, ...lintels], design => {
        const element = guidToElement[design.wall_guid]
        return visibleStoreys.has(element.storey)
      }),
    [rips, lintels, guidToElement, visibleStoreys],
  )

  const verticalSlabsInStorey = useMemo(
    () =>
      filter(verticalSlabs, verticalSlab => {
        const element = guidToElement[verticalSlab.guid]
        return visibleStoreys.has(element.storey)
      }),
    [verticalSlabs, guidToElement, visibleStoreys],
  )

  const verticalRoofSlabsInStorey = useMemo(
    () =>
      filter(verticalRoofSlabs, verticalSlab => {
        const element = guidToElement[verticalSlab.guid]
        return visibleStoreys.has(element.storey)
      }),
    [verticalRoofSlabs, guidToElement, visibleStoreys],
  )

  const tensileTransmissionGraphStorey = useMemo(
    () => ({
      element_supports: filter(tensileTransmissionGraph?.element_supports, support => {
        const element = guidToElement[support.element_guid]
        if (!element) return true
        return visibleStoreys.has(element.storey)
      }),
      element_targets: filter(tensileTransmissionGraph?.element_targets, support => {
        const element = guidToElement[support.element_guid]
        if (!element) return true
        return visibleStoreys.has(element.storey)
      }),
      support_targets: filter(tensileTransmissionGraph?.support_targets, support => {
        const element = guidToElement[support.target_guid]
        if (!element) return true
        return visibleStoreys.has(element.storey)
      }),
    }),
    [tensileTransmissionGraph, visibleStoreys, guidToElement],
  )

  const stiffeningIntervalsInStorey = useMemo(
    () =>
      filter(stiffeningIntervals, interval => {
        const element = guidToElement[interval.element_guid]
        if (!element) return true
        return visibleStoreys.has(element.storey)
      }),
    [stiffeningIntervals, visibleStoreys, guidToElement],
  )

  const selectedPositionType = useElementPositionType(selectedElement || undefined)

  const slabBeams = useMemo(
    () => [...verticalSlabs, ...verticalRoofSlabs].map(slab => slab.beam),
    [verticalSlabs, verticalRoofSlabs],
  )

  const allLoadsWithDomain = useEnhanceLoadsWithDomain({
    loads: loads,
    domains: allDomains,
    verticalTransmissionGraph: verticalTransmissionGraph,
    slabBeams,
  })

  const allPositionsInSlabs = useMemo(() => {
    const slab_beams =
      (verticalSlabsInStorey && flatten(verticalSlabsInStorey.map(({ beam }) => beam))) || []
    const roof_rafters =
      (verticalRoofSlabsInStorey && flatten(verticalRoofSlabsInStorey.map(({ beam }) => beam))) ||
      []
    const all_beams = [...slab_beams, ...roof_rafters]
    return all_beams
  }, [verticalSlabsInStorey, verticalRoofSlabsInStorey])

  const guidToPosition = useMemo(
    () => wallDesignsInStorey && mapValueKey(wallDesignsInStorey, 'position_guid'),
    [wallDesignsInStorey],
  )

  const slabBeamGuidToPosition = useMemo(
    () => allPositionsInSlabs && mapValueKey(allPositionsInSlabs),
    [allPositionsInSlabs],
  )

  // The selected vertical (roof) slab (selection stores the surface guid). Drives whether the
  // scene shows the surface's own loads (Dach/Decke tab) or the slab beam's loads (Bemessung).
  const selectedSlab = useMemo(() => {
    if (!selectedElement) return undefined
    return (
      verticalSlabs.find(s => s.guid === selectedElement) ??
      verticalRoofSlabs.find(s => s.guid === selectedElement)
    )
  }, [selectedElement, verticalSlabs, verticalRoofSlabs])

  const loadsOnElement = useMemo(() => {
    // This will have length if a standard-rip is selected, so we display those
    // loads instead
    if (selectedStandardRipLoads.length > 0) return selectedStandardRipLoads

    if (!guidToPosition || !slabBeamGuidToPosition) return

    let elementGuid: string | undefined
    if (selectedSlab) {
      // Dach/Decke tab → the surface's own loads; any other tab (Bemessung) → the beam's loads.
      elementGuid = detailTab === 'surface' ? selectedSlab.guid : selectedSlab.beam?.guid
    } else if (selectedElement) {
      if (selectedElement in guidToPosition) {
        const position = guidToPosition[selectedElement]
        elementGuid = position.position_guid
      } else if (selectedElement in slabBeamGuidToPosition) {
        const position = slabBeamGuidToPosition[selectedElement]
        // slab beams just have a guid
        elementGuid = position.guid
      } else {
        elementGuid = selectedElement
      }
    }

    return elementGuid
      ? filter(
          allLoadsWithDomain,
          load =>
            load.element_guid === elementGuid &&
            (isUndefined(localTab) || (visibleLoadTypes[load.load_type as LoadType] ?? true)),
        )
      : []
  }, [
    allLoadsWithDomain,
    selectedElement,
    detailTab,
    selectedSlab,
    guidToPosition,
    slabBeamGuidToPosition,
    visibleLoadTypes,
    localTab,
    selectedStandardRipLoads,
  ])

  // Trace mode: the traced (selected row's) loads plus their transitive previous loads, found
  // by following previous_load_guids across elements until a load has no predecessor anymore.
  const loadTrace = useMemo(() => {
    if (!tracedLoads?.length || !allLoadsWithDomain) return null

    const guidToLoad = new Map(allLoadsWithDomain.map(load => [load.guid, load]))
    const selected = tracedLoads
      .map(guid => guidToLoad.get(guid))
      .filter((load): load is AreaLoad | DomainLoad => !!load)

    const visited = new Set(tracedLoads)
    const previous: (AreaLoad | DomainLoad)[] = []
    let frontier = selected
    while (frontier.length > 0) {
      const next: (AreaLoad | DomainLoad)[] = []
      frontier.forEach(load => {
        load.previous_load_guids?.forEach(previousGuid => {
          if (visited.has(previousGuid)) return
          visited.add(previousGuid)
          const previousLoad = guidToLoad.get(previousGuid)
          if (previousLoad) {
            previous.push(previousLoad)
            next.push(previousLoad)
          }
        })
      })
      frontier = next
    }

    return { selected, previous }
  }, [tracedLoads, allLoadsWithDomain])

  const loadsCombined = useLoadsCombinedStore(state => state.loadsCombined)

  const loadsOnElementCombined = useMemo(
    () =>
      loadsCombined ? combineAllLoadsByLoadCaseAndPosition(loadsOnElement || []) : loadsOnElement,
    [loadsCombined, loadsOnElement],
  )

  const loadsOnElementCombinedAndFiltered = useMemo(
    () =>
      loadsOnElementCombined
        ? filter(loadsOnElementCombined, load => !hiddenLoads.includes(load.guid))
        : [],
    [loadsOnElementCombined, hiddenLoads],
  )

  const visibleAreaLoads = useMemo(
    () =>
      filter(
        manualLoads,
        load =>
          load.load_type === 'area-load' &&
          areaLoadsVisible &&
          (visibleLoadTypes[load.load_type] ?? true) &&
          !hiddenLoads.includes(load.guid),
      ) as AreaLoad[],
    [manualLoads, areaLoadsVisible, visibleLoadTypes, hiddenLoads],
  )

  // The loads drawn on elements in 3D, for the 2D floorplan (which draws the manual area loads
  // itself). A trace's previous loads stay 3D-only: they sit on other elements, mostly of other
  // storeys, so they would be misleading in a floorplan.
  const floorplanElementLoads = useMemo(() => {
    const elementLoads = isLocalMode ? loadTrace?.selected ?? loadsOnElementCombinedAndFiltered : []
    return [
      ...(isAnchorsTabActive ? anchorReducingLoads : []),
      ...(isShearAnchorsTabActive ? shearAnchorLoads : []),
      ...elementLoads.filter((load): load is DomainLoad => load.load_type !== 'area-load'),
    ]
  }, [
    isLocalMode,
    loadTrace,
    loadsOnElementCombinedAndFiltered,
    isAnchorsTabActive,
    anchorReducingLoads,
    isShearAnchorsTabActive,
    shearAnchorLoads,
  ])

  // over the drawn loads including the hidden ones of the element, so hiding loads keeps the sizes
  const floorplanScaledLoads = useMemo(
    () => [...floorplanElementLoads, ...(isLocalMode ? loadsOnElementCombined ?? [] : [])],
    [floorplanElementLoads, isLocalMode, loadsOnElementCombined],
  )
  const floorplanMaxLineLoadMagnitude = useMemo(
    () =>
      maxLineLoadMagnitude(
        floorplanScaledLoads.filter(
          (load): load is LineLoadWithDomain => load.load_type === 'line-load',
        ),
      ),
    [floorplanScaledLoads],
  )
  const floorplanMaxPointLoadMagnitude = useMemo(
    () =>
      maxPointLoadMagnitude(
        floorplanScaledLoads.filter(
          (load): load is PointLoadWithDomain => load.load_type === 'point-load',
        ),
      ),
    [floorplanScaledLoads],
  )

  // The wall the selected anchor belongs to. It gets the dark-blue frame in the scene (as the
  // legacy Zugverbindertabelle's connector did), so at wall corners it is clear which wall a
  // tensile anchor of the "Anker" tab or a shear anchor of the "Schubanker" tab belongs to.
  const selectedAnchorWallGuid = useMemo(() => {
    if (isShearAnchorsTabActive && selectedShearAnchor) {
      return getWallGuidForStiffeningSegment(stiffeningIntervals, selectedShearAnchor) ?? null
    }

    const supportGuid = selectedConnector ?? (isAnchorsTabActive ? selectedAnchor : null)
    if (!supportGuid || !tensileTransmissionGraph) return null

    const selectedSupport = find(tensileTransmissionGraph.element_supports, { guid: supportGuid })

    return selectedSupport?.element_guid ?? null
  }, [
    isShearAnchorsTabActive,
    selectedShearAnchor,
    stiffeningIntervals,
    selectedConnector,
    selectedAnchor,
    isAnchorsTabActive,
    tensileTransmissionGraph,
  ])

  // EVENTS

  const guardedMultiSelectAdd = useCallback(
    (elementIds: string[]) => {
      if (guardDesignFormNav()) return
      multiSelectElement(elementIds)
    },
    [guardDesignFormNav, multiSelectElement],
  )

  const guardedMultiSelectRemove = useCallback(
    (elementId: string) => {
      if (guardDesignFormNav()) return
      removeMultiSelectElement(elementId)
    },
    [guardDesignFormNav, removeMultiSelectElement],
  )

  useMultiSelection({
    addElements: guardedMultiSelectAdd,
    removeElement: guardedMultiSelectRemove,
    selectedElements: isArray(selectedElements) ? (selectedElements as string[]) : [],
  })

  const onClickElement = useCallback(
    (guid: string, elementType: ResultElementType | WallMeshElementType) => {
      // Slab/roof beams already report their surface guid (PositionMemberMeshElement selectionGuid),
      // so `guid` is always the selection identifier here.
      if (guid !== selectedElement && guardDesignFormNav()) return

      const groupingElements: PositionGroupingType[] = ['beams', 'columns', 'purlins']

      if (elementType === 'rip') {
        selectPosition(guid, 'walls', 'wall-rips')
      } else if (elementType === 'lintel') {
        selectPosition(guid, 'walls', 'wall-lintels')
      } else if (elementType === 'slab_beams') {
        selectPosition(guid, 'slab-beams')
      } else if (elementType === 'roof_slab_beams') {
        selectPosition(guid, 'roof-slab-beams')
      } else if (groupingElements.includes(elementType as PositionGroupingType)) {
        selectPosition(guid, elementType as ResultsViewTab)
      } else if (elementType === 'inner_walls' || elementType === 'outer_walls') {
        selectPosition(guid, 'walls', 'wall-assemblies')
      } else if (elementType === 'vertical_slabs') {
        // Clicking a Decke stores its guid and opens the merged "Decke" tab.
        const beamGuid = find(verticalSlabs, { guid })?.beam?.guid
        if (beamGuid) selectSurface(guid, 'slab-beams')
        else selectPosition(guid, 'slabs')
      } else if (elementType === 'vertical_roof_slabs') {
        // Clicking a Dach stores its guid and opens the merged "Dach" tab.
        const beamGuid = find(verticalRoofSlabs, { guid })?.beam?.guid
        if (beamGuid) selectSurface(guid, 'roof-slab-beams')
        else selectPosition(guid, 'roof-slabs')
      }
    },
    [
      guardDesignFormNav,
      selectPosition,
      selectSurface,
      selectedElement,
      verticalSlabs,
      verticalRoofSlabs,
    ],
  )

  const latestOnClickElement = useLatest(onClickElement)

  useModelClickListeners(
    event => {
      const { name: guid } = event.object
      const { elementType } = event.object.userData as {
        elementType: ResultElementType | WallMeshElementType
      }

      latestOnClickElement.current(guid, elementType)
    },
    [latestOnClickElement],
    !isSelectionMode && !isTapelineActive && cornerConnectionState.type === 'idle',
  )

  const supportGuidToElement = useSupportGuidToElement(tensileTransmissionGraph)

  useModelClickListeners(
    event => {
      if (cornerConnectionState.type !== 'selected-anchor') return

      const { elementType } = event.object.userData as {
        elementType: ElementTypes
      }
      if (elementType !== 'inner_walls' && elementType !== 'outer_walls') return

      const supportElement = supportGuidToElement?.[cornerConnectionState.anchorGuid]

      const wallGuid = event.object.name

      if (supportElement?.guid === wallGuid) return

      const { field } = cornerConnectionState

      const newState = selectWall(wallGuid)
      unsetSelectionMode()
      if (newState) {
        mutateCornerConnectionProposal({
          anchorGuid: newState?.anchorGuid,
          wallGuid,
          field,
        })
      }
    },
    [selectWall, unsetSelectionMode],
    cornerConnectionState.type === 'selected-anchor',
  )

  // EFFECTS

  const beamIds = useMemo(() => model.beams.map(({ guid }) => guid), [model])
  const columnIds = useMemo(() => model.columns.map(({ guid }) => guid), [model])
  const purlinIds = useMemo(() => model.purlins.map(({ guid }) => guid), [model])

  const tracedSourceGuid = useMemo(() => {
    if (!selectedLoad) return undefined
    const load = find(loads, { guid: selectedLoad })
    return load?.user_shown_source ?? undefined
  }, [loads, selectedLoad])

  useEffect(() => {
    const hiddenIds = [
      ...(selectedPositionType === 'beams' ? [] : beamIds),
      ...(selectedPositionType === 'columns' ? [] : columnIds),
      ...(selectedPositionType === 'purlins' ? [] : purlinIds),
    ]

    // Don't hide elements when coordinate input mode is active (comment mode coordinate input)
    const isCoordinateInputMode = selectionModeKey?.startsWith(coordinateEventName)
    // Don't hide elements while picking a point for a comment
    const isAddCommentMode = selectionModeKey === ADD_COMMENTS_SELECTION_KEY

    if (
      isSelectionMode &&
      selectionModeKey !== proposalSelectionKey &&
      !isCoordinateInputMode &&
      !isAddCommentMode
    ) {
      addHiddenElementIds(hiddenIds)
    } else {
      removeHiddenElementIds(hiddenIds)
    }
  }, [
    isSelectionMode,
    addHiddenElementIds,
    removeHiddenElementIds,
    beamIds,
    columnIds,
    purlinIds,
    selectedPositionType,
    selectionModeKey,
    proposalSelectionKey,
  ])

  const verticalTransmitters = useVerticalTransmitters(
    verticalTransmissionGraph,
    selectedElement || null,
  )

  const positionGuidToWallGuid = usePositionGuidToWallGuid()
  // if the selectedLoad has a mapping element in the LoadTracingMap it will be
  // highlighted in the model
  const highlightedSecondary = useMemo(() => {
    if (
      selectedElement &&
      (selectedPositionType === 'wall-rips' || selectedPositionType === 'wall-lintels')
    ) {
      const wallGuid = positionGuidToWallGuid[selectedElement]
      return [wallGuid]
    }
    if (tracedSourceGuid) {
      return [tracedSourceGuid]
    } else if (selectedElement && verticalTransmitters) {
      const targetGuids = verticalTransmitters.reduce(
        (acc, item: ElementSupportItemWithTargets) => {
          return [...acc, ...item.targets.map(({ element_guid }) => element_guid)]
        },
        [] as string[],
      )

      return targetGuids
    }

    if (selectedAnchorWallGuid) {
      return [selectedAnchorWallGuid]
    }

    return []
  }, [
    selectedElement,
    tracedSourceGuid,
    verticalTransmitters,
    selectedAnchorWallGuid,
    selectedPositionType,
    positionGuidToWallGuid,
  ])

  // SCENE COLORING

  const highlightedPrimary = useMemo(() => {
    return cornerConnectionState.type === 'selected-anchor-and-wall' ||
      cornerConnectionState.type === 'proposal-created' ||
      cornerConnectionState.type === 'proposal-changed'
      ? [cornerConnectionState.wallGuid]
      : selectedAnchorWallGuid
      ? [selectedAnchorWallGuid]
      : []
  }, [cornerConnectionState, selectedAnchorWallGuid])

  useLayoutEffect(() => {
    if (cornerConnectionState.type !== 'idle') return

    const highlightedIds = selectedAnchorWallGuid
      ? [...selectedElements, selectedAnchorWallGuid]
      : selectedElements

    setSelectedStandaloneIds(highlightedIds)

    return () => setSelectedStandaloneIds([])
  }, [
    cornerConnectionState.type,
    selectedElements,
    selectedAnchorWallGuid,
    setSelectedStandaloneIds,
  ])
  useHighlightPrimary(highlightedPrimary, cornerConnectionState.type !== 'idle')
  useHighlightSecondary(highlightedSecondary, cornerConnectionState.type !== 'idle')
  useHighlightSecondary(
    highlightedSecondary,
    selectedPositionType === 'wall-rips' || selectedPositionType === 'wall-lintels',
  )

  useSceneColoring()

  const selectedShapeObject = useMemo(() => {
    if (selectedPositionType === 'beams') {
      return model.beams.find(beam => beam.guid === selectedElement)
    } else if (selectedPositionType === 'purlins') {
      return model.purlins.find(purlin => purlin.guid === selectedElement)
    } else if (selectedPositionType === 'columns') {
      return model.columns.find(columns => columns.guid === selectedElement)
    } else if (selectedPositionType === 'roof-slab-beams') {
      // Selection stores the surface guid; accept the beam guid too for robustness.
      const selectedRoofSlab = model.vertical_roof_slabs.find(
        roof_slab => roof_slab.guid === selectedElement || roof_slab.beam?.guid === selectedElement,
      )
      return selectedRoofSlab?.beam
    } else if (selectedPositionType === 'slab-beams') {
      // Selection stores the surface guid; accept the beam guid too for robustness.
      const selectedSlab = model.vertical_slabs.find(
        slab => slab.guid === selectedElement || slab.beam?.guid === selectedElement,
      )
      return selectedSlab?.beam
    }
  }, [
    selectedPositionType,
    model.beams,
    model.purlins,
    model.columns,
    model.vertical_roof_slabs,
    model.vertical_slabs,
    selectedElement,
  ])

  const selectedCheckPositionIsNumeric =
    selectedCheckPosition !== null && selectedCheckPosition !== undefined

  const selectedRipOrLintelMarkerPosition = useMemo(() => {
    if (
      !selectedElement ||
      !selectedCheckPositionIsNumeric ||
      (selectedPositionType !== 'wall-rips' && selectedPositionType !== 'wall-lintels')
    ) {
      return null
    }
    const checkPosition = toNumber(selectedCheckPosition)
    if (selectedPositionType === 'wall-rips' && rips) {
      const rip = rips.find(r => r.position_guid === selectedElement)
      if (rip) {
        return getPositionVectorFromCheckPosition(rip.start, rip.end, checkPosition, 'rips')
      }
    }
    if (selectedPositionType === 'wall-lintels' && lintels) {
      const lintel = lintels.find(l => l.position_guid === selectedElement)
      if (lintel) {
        return getPositionVectorFromCheckPosition(
          lintel.start,
          lintel.end,
          checkPosition,
          'lintels',
        )
      }
    }
    return null
  }, [
    selectedElement,
    selectedCheckPositionIsNumeric,
    selectedCheckPosition,
    selectedPositionType,
    rips,
    lintels,
  ])

  const handleStiffeningSegmentClick = (guid: string | undefined) => {
    if (!guid) return
    if (guid === selectedStiffeningSegment) {
      const interval = stiffeningIntervals.find(interval => interval.guid === guid)
      if (interval) selectPosition(interval.element_guid, 'walls', 'wall-assemblies')
    } else selectStiffeningSegment(guid)
  }
  // SCENE COLORING

  useEffect(() => {
    const standardRipCheck = standardRipChecks?.find(
      check => check.standard_rip_member.guid === selectedElement,
    )
    if (!standardRipCheck) return

    if (selectedStandAloneIds && first([...selectedStandAloneIds]) === standardRipCheck.wall_guid) {
      // Check if the highlighted element is already the wall to avoid render-loop
      return
    }
    // Only set if wall-guid is different. Only highlight one element.
    setSelectedStandaloneIds([standardRipCheck.wall_guid])
  }, [
    selectedElement,
    standardRipChecks,
    tab,
    isLocalMode,
    selectedStandAloneIds,
    setSelectedStandaloneIds,
  ])

  // Shortcuts (disabled when typing in anchor table)
  const shortcutsEnabled = !isAnchorCalculationMode

  useHotkeys_0to9WithNumpadAndLongPress(
    storeyKey => {
      if (availableNonRoofStoreys.includes(storeyKey)) toggleSingleStoreyVisibility(storeyKey)
    },
    storeyKey => {
      if (availableNonRoofStoreys.includes(storeyKey)) {
        availableNonRoofStoreys.forEach(storey => {
          setStoreyVisibility(storey, false)
        })
        setStoreyVisibility(roofStoreyKey, false)
        setStoreyVisibility(storeyKey, true)
      }
    },
    { enabled: shortcutsEnabled },
    [model, shortcutsEnabled],
  )

  useHotkeys_ShortAndLongPress(
    ['r'],
    () => {
      setTypeVisibility('vertical_roof_slabs' as ElementTypes, !showVerticalRoofSlabs)
    },
    () => {
      setTypeVisibility('roof_slabs' as ElementTypes, !showRoofSlabs)
    },
    { enabled: shortcutsEnabled },
    [model, showVerticalRoofSlabs, showRoofSlabs, shortcutsEnabled],
  )

  useHotkeys(
    ['shift+r'],
    () => toggleSingleStoreyVisibility(roofStoreyKey),
    { enabled: shortcutsEnabled },
    [model, showVerticalRoofSlabs, showRoofSlabs, shortcutsEnabled],
  )

  useHotkeys(
    ['p'],
    () => {
      setTypeVisibility('purlins' as ElementTypes, !showPurlins)
    },
    { enabled: shortcutsEnabled },
    [model, showPurlins, shortcutsEnabled],
  )

  useHotkeys_ShortAndLongPress(
    ['d'],
    () => {
      setTypeVisibility('vertical_slabs' as ElementTypes, !showVerticalSlabs)
    },
    () => {
      setTypeVisibility('slabs' as ElementTypes, !showSlabs)
    },
    { enabled: shortcutsEnabled },
    [model, showVerticalSlabs, showSlabs, shortcutsEnabled],
  )

  useHotkeys(
    ['c'],
    () => {
      setShowComments(!showComments)
    },
    { enabled: shortcutsEnabled },
    [showComments, shortcutsEnabled],
  )

  const proposalConfig = useMemo(() => {
    if (
      cornerConnectionState.type !== 'proposal-created' &&
      cornerConnectionState.type !== 'proposal-changed'
    )
      return

    const wall = guidToElement[cornerConnectionState.wallGuid] as ShapeObject
    const { leftInterval, rightInterval } = createProposalConfig(
      wall,
      cornerConnectionState.proposal.left_interval,
      cornerConnectionState.proposal.right_interval,
    )

    return { leftInterval, rightInterval }
  }, [cornerConnectionState, guidToElement])

  const handleLintelClick = useCallback(
    (guid: string) => {
      if (cornerConnectionState.type !== 'idle') return
      if (isSelectionMode) return
      latestOnClickElement.current(guid, 'lintel')
    },
    [cornerConnectionState.type, isSelectionMode, latestOnClickElement],
  )

  const handleRipClick = useCallback(
    (guid: string) => {
      if (cornerConnectionState.type !== 'idle') return
      if (isSelectionMode) return
      latestOnClickElement.current(guid, 'rip')
    },
    [cornerConnectionState.type, isSelectionMode, latestOnClickElement],
  )

  const lintelGuids = useMemo(() => lintels?.map(lintel => lintel.position_guid), [lintels])
  const ripGuids = useMemo(() => rips?.map(rip => rip.position_guid), [rips])

  useModelClickListenerV2('position', handleRipClick, {
    enabled: actionMode !== 'hide',
    guids: ripGuids,
  })
  useModelClickListenerV2('position', handleLintelClick, {
    enabled: actionMode !== 'hide',
    guids: lintelGuids,
  })

  const elementTypesRef = useLatest(elementTypes)
  useFloorplanClickListener(guid => {
    const elementType = elementTypesRef.current[guid]
    if (!elementType) return

    let mappedElementType: ResultElementType | WallMeshElementType | undefined

    if (elementType === 'rips') mappedElementType = 'rip'
    else if (elementType === 'lintels') mappedElementType = 'lintel'
    else if (elementType === 'standard_rips') return
    else if (elementType === 'foundation') return
    else {
      mappedElementType = elementType
    }

    if (cornerConnectionState.type !== 'idle') return
    if (isSelectionMode) return
    latestOnClickElement.current(guid, mappedElementType)
  })

  const storey = useControlStore(state => state.floorplanState.storey)
  const showFloorplan = storey !== null

  const allHiddenIds = useMemo(
    () => [...hiddenElementIds, ...hiddenElements],
    [hiddenElementIds, hiddenElements],
  )

  return (
    <>
      <group visible={!showFloorplan}>
        {/* One "Vertikale Verbindungen" toggle: shows the selected element's connections when
            something is selected, otherwise all connections (empty elementGuids ⇒ no filter). The
            active-transmitter highlight only applies on the Vertikale Verbindungen tab of a selection. */}
        {verticalTransmittersVisible && verticalTransmissionGraph && (
          <TransmitterMesh
            data={verticalTransmissionGraph}
            pointConnectorKind="vertical"
            domains={modelDomains}
            transmitterGuid={
              selectedElements?.length && detailTab === 'vertical-connections'
                ? (verticalTransmitter as string)
                : undefined
            }
            onClick={({ guid }) => setVerticalTransmitter(guid)}
            elementGuids={selectedElements as string[]}
            hiddenElements={hiddenElements}
          />
        )}

        {tensileTransmissionGraphStorey && isAnchorCalculationMode && (
          <>
            <TransmitterMesh
              data={tensileTransmissionGraphStorey}
              onClick={({ guid }) => {
                if (cornerConnectionState.type !== 'idle') return
                selectConnector(guid)
              }}
              domains={allDomains}
              transmitterGuid={selectedConnector || undefined}
            />
          </>
        )}

        {((tab === 'walls' && wallSubTab === 'stiffening') ||
          isAnchorCalculationMode ||
          isAnchorsTabActive) && (
          <SimpleStiffeningMesh
            model={model}
            intervals={stiffeningIntervalsInStorey}
            onClick={guid => {
              if (cornerConnectionState.type !== 'idle') return
              handleStiffeningSegmentClick(guid)
            }}
            highlightedIds={selectedStiffeningSegment ? [selectedStiffeningSegment] : undefined}
            nonSelectable={cornerConnectionState.type !== 'idle'}
          />
        )}

        {resultStoreVisibility.anchorIndicators && anchorChecks && (
          <AnchorIndicatorMesh
            graph={tensileTransmissionGraphStorey}
            domains={modelDomains}
            anchorChecks={anchorChecks}
          />
        )}

        {isAnchorsTabActive && anchorReducingLoads.length > 0 && (
          <SimpleLoadMesh loads={anchorReducingLoads} />
        )}

        {/* All tensile anchors are shown while the "Anker" tab is active; the selected one pulses
            (same active-connector highlight the legacy Zugverbindertabelle used). */}
        {isAnchorsTabActive && !isAnchorCalculationMode && (
          <TransmitterMesh
            data={tensileTransmissionGraphStorey}
            domains={allDomains}
            transmitterGuid={selectedAnchor || undefined}
            onClick={handleAnchorConnectorClick}
            // Connector clicks stopPropagation even when the handler ignores them, so they must be
            // fully disabled during the "Wand über Eck" flow — otherwise a connector at the wall
            // corner swallows the wall pick.
            disableClick={cornerConnectionState.type !== 'idle'}
          />
        )}

        {isShearAnchorsTabActive && shearAnchorLoads.length > 0 && (
          <SimpleLoadMesh loads={shearAnchorLoads} />
        )}

        <>
          {rips?.map(rip => (
            <PositionMeshConnector
              guid={rip.position_guid}
              key={rip.position_guid}
              visible={positionVisibilityLookup[rip.position_guid]}
              selected={selectedElements?.includes(rip.position_guid)}
              highlighted={tracedSourceGuid === rip.position_guid}
              target={highlightedSecondaryIds.has(rip.position_guid)}
            />
          ))}

          {lintels?.map(lintel => (
            <PositionMeshConnector
              key={lintel.position_guid}
              guid={lintel.position_guid}
              visible={positionVisibilityLookup[lintel.position_guid]}
              selected={selectedElements?.includes(lintel.position_guid)}
              highlighted={tracedSourceGuid === lintel.position_guid}
              target={highlightedSecondaryIds.has(lintel.position_guid)}
            />
          ))}
        </>

        {verticalSlabsInStorey && showSlabBeams && (
          <SlabBeamsWithStepSize
            data={verticalSlabsInStorey}
            selectedElement={selectedElement || undefined}
            highlightedElements={tracedSourceGuid ? [tracedSourceGuid] : undefined}
            onClick={(d, e) => {
              if (cornerConnectionState.type !== 'idle') return
              e.object.userData.elementType = 'slab_beams'
              return onModelClick(e)
            }}
            hiddenIds={allHiddenIds}
            selectViaSurfaceGuid
          />
        )}

        {verticalRoofSlabsInStorey && showRoofSlabBeams && (
          <SlabBeamsWithStepSize
            data={verticalRoofSlabsInStorey}
            selectedElement={selectedElement || undefined}
            highlightedElements={tracedSourceGuid ? [tracedSourceGuid] : undefined}
            onClick={(d, e) => {
              if (cornerConnectionState.type !== 'idle') return
              e.object.userData.elementType = 'roof_slab_beams'
              return onModelClick(e)
            }}
            hiddenIds={allHiddenIds}
            selectViaSurfaceGuid
          />
        )}

        <SimpleLoadMesh
          loads={visibleAreaLoads}
          onClick={({ guid }) => {
            if (cornerConnectionState.type !== 'idle') return
            selectLoad(guid)
          }}
          highlightedIds={selectedLoad ? [selectedLoad] : tracedLoads ?? undefined}
        />

        {isLocalMode && loadTrace ? (
          <>
            <SimpleLoadMesh
              loads={loadTrace.selected.filter(load => load.load_type !== 'area-load')}
              highlightedIds={tracedLoads ?? undefined}
            />
            <SimpleLoadMesh
              loads={loadTrace.previous.filter(load => load.load_type !== 'area-load')}
              opacity={PREVIOUS_LOAD_OPACITY}
            />
          </>
        ) : (
          isLocalMode && (
            <SimpleLoadMesh
              loads={loadsOnElementCombinedAndFiltered.filter(
                load => load.load_type !== 'area-load',
              )}
              onClick={({ guid }) => {
                if (cornerConnectionState.type !== 'idle') return
                selectLoad(guid)
              }}
              highlightedIds={selectedLoad ? [selectedLoad] : undefined}
            />
          )
        )}

        <WindLoadCPEAreasMesh />

        {showVerticalOpeningAlignmentHelpers && (
          <AlignmentHelper verticalOpeningHelpers={verticalOpeningHelpers} />
        )}

        <GroupingLabels selectedElement={selectedElement} />

        <SupportSceneLabels
          items={supportLabelItems}
          elementGuidForSupports={selectedElementGuidForSupports}
          showFloorplan={showFloorplan}
        />

        <CheckPositionSceneLabels
          items={checkMarkerItems}
          elementGuidForChecks={selectedElementGuidForSupports}
          showFloorplan={showFloorplan}
        />

        {selectedPositionType &&
          ['beams', 'purlins', 'columns', 'roof-slab-beams', 'slab-beams'].includes(
            selectedPositionType,
          ) &&
          selectedShapeObject &&
          selectedCheckPositionIsNumeric && (
            <SupportPositionIndicatorMesh
              position={toImmutable(
                interpolateShapeObject(selectedShapeObject, toNumber(selectedCheckPosition)),
              )}
              color="red"
              visible={true}
            />
          )}

        {selectedRipOrLintelMarkerPosition && (
          <SupportPositionIndicatorMesh
            position={toImmutable(selectedRipOrLintelMarkerPosition)}
            color="red"
            visible={true}
          />
        )}

        {/* Main proposal config */}

        {(cornerConnectionState.type === 'proposal-created' ||
          cornerConnectionState.type === 'proposal-changed') &&
          selectedProposalAnchor === cornerConnectionState.anchorGuid &&
          proposalConfig?.leftInterval && (
            <CornerConnectionProposalMesh
              points={proposalConfig.leftInterval}
              color="#a232a8"
              label="1"
            />
          )}

        {(cornerConnectionState.type === 'proposal-created' ||
          cornerConnectionState.type === 'proposal-changed') &&
          selectedProposalAnchor === cornerConnectionState.anchorGuid &&
          proposalConfig?.rightInterval && (
            <CornerConnectionProposalMesh
              points={proposalConfig?.rightInterval}
              color="#a8326b"
              label="2"
            />
          )}
      </group>

      {showFloorplan && (
        <FloorplanLoadsMesh
          loads={floorplanElementLoads}
          maxLineLoadMagnitude={floorplanMaxLineLoadMagnitude}
          maxPointLoadMagnitude={floorplanMaxPointLoadMagnitude}
        />
      )}
    </>
  )
}

export default Results2Scene
